import { AxiosResponse } from 'axios'
import { useSearchParams } from 'next/navigation'
import { useEffect, useMemo, useRef, useState, useCallback, startTransition } from 'react'

import { SVD_ENRICH_VERSION_HEADER, SVD_ENRICH_VERSION_VALUE } from '@src/config/svd-flags'
import { SUBSCRIPTION_EXP_ENABLED_HEADER } from '@src/constants/api.headers.constants'
import { useSearchFiltersV2Experiment } from '@src/experiments/statsig-integration/use-search-filters-v2-experiment.hook'
import {
  SelectedFilter,
  Filter,
  UpdateQueryParams,
} from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'
import { MODULES, FEATURES } from '@src/quince-site-common/src/constants/template-info'
import { useIsSvdEnabled } from '@src/quince-site-common/src/hooks/use-is-svd-enabled'
import useIntersect from '@src/quince-site-common/src/hooks/useIntersect'
import { useAllQueryState } from '@src/quince-site-common/src/hooks/useQueryState'
import searchCacheStateManager from '@src/quince-site-common/src/redux/globalStateManager/SearchCacheStateManager'
import siteStateManager, { SearchDetails } from '@src/quince-site-common/src/redux/globalStateManager/SiteStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import {
  fetchSearchLayout,
  fetchSearchWidgetsData,
} from '@src/quince-site-common/src/services/sandSearchService/searchService'
import {
  LayoutResponse,
  PAGE_TYPES,
  WidgetFetchDataResponse,
} from '@src/quince-site-common/src/services/sandSearchService/searchService.types'
import { getContentProductsEnv } from '@src/quince-site-common/src/utils/getEnvironmentParam'
import { getLbUserIdForAlgolia } from '@src/quince-site-common/src/utils/getlb-user-id'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { SEARCH_PRODUCT_CARD_BANDS } from '@src/scroll-restoration/bands'
import { useBackNavScrollRestoration } from '@src/scroll-restoration/use-back-nav-scroll-restoration'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { shouldUseDesignSystem } from '@src/utils/should-use-design-system'

import { SEARCH_WATCHED_QUERY_KEYS, SEARCH_LIMIT as LIMIT } from '../searchResults/searchResults.constants'
import {
  convertFacetsToInitialFilters,
  getSearchCardKey,
  getSearchCompletedEventData,
  getSearchFilters,
  getUpdatedSelectedFilters,
  resetSearchCacheOnFilterChange,
  scrollSearchResultsIntoViewIfAbove,
  scrollToTop,
  transformProductsToProductCards,
  type SearchProductCard,
  type TSearchQueryState,
} from '../utils'

import useSearchCache from './useSearchCache'

// Error messages constants
const FILTER_UPDATE_ERROR_MESSAGE = 'Failed to apply search filter'
const INITIAL_SEARCH_ERROR_MESSAGE = 'Failed to fetch initial search results'
const LOAD_MORE_ERROR_MESSAGE = 'Failed to fetch more search results'

export const useSearchResults = (subscriptionExpEnabled?: boolean) => {
  // Treatment collapses the two clocks this hook used to run on (state for the refetch trigger, URL
  // for the cache branch) and swaps hide-while-loading for dim-while-refetching. Control is the
  // pre-experiment behaviour, including its known defects, so the readout is clean.
  const filtersV2Enabled = useSearchFiltersV2Experiment()
  const [watchedQueryStates, setWatchedQueryStates] = useAllQueryState<'filter' | 'q'>(
    SEARCH_WATCHED_QUERY_KEYS,
    undefined,
    false,
    true,
    // `deferNavigation` (both buckets; rides the existing INP scheduling gate): the chip reads the
    // state write and paints in the click's frame, the `router.push` (forced layout) runs in the next
    // task. Treatment never waits on the URL, since its cache is keyed off the same state; control's
    // URL-echo fetch simply starts one task later.
    { skipIdenticalUrlEcho: filtersV2Enabled, deferNavigation: true },
  )
  const searchParams = useSearchParams()
  const lbUserId = getLbUserIdForAlgolia()
  const abortControllerRef = useRef<AbortController | null>(null)

  // TEMPORARY (STRFRNT-15810/STRFRNT-16013): ask PL for SVD-shaped cards (carrying the Algolia
  // `searchObjectId` attribution field) on every search fetch — see `SVD_ENRICH_VERSION_HEADER` doc.
  const svdEnrichHeaderEnabled = useIsSvdEnabled()
  const searchRequestHeaders = useMemo(
    () => ({
      [SUBSCRIPTION_EXP_ENABLED_HEADER]: subscriptionExpEnabled ? 'true' : 'false',
      ...(svdEnrichHeaderEnabled ? { [SVD_ENRICH_VERSION_HEADER]: SVD_ENRICH_VERSION_VALUE } : {}),
    }),
    [subscriptionExpEnabled, svdEnrichHeaderEnabled],
  )

  // Extract selected filters option from URL
  const { selectedFilters, searchQuery } = useMemo(() => {
    const selectedFilters = watchedQueryStates.filter
      ? SelectedFilter.getSelectedFilterFromQueryParams(watchedQueryStates.filter)
      : new SelectedFilter({})
    const searchQuery = watchedQueryStates.q ?? ''

    return { selectedFilters, searchQuery }
  }, [watchedQueryStates])

  // Treatment keys the cache off the same state that triggers the refetch effect below. Control keeps
  // the URL-derived key, which lags a filter click by a render (`router.push` is async) and so hits
  // the PREVIOUS results on the first click; the URL echo re-run is what fetches there.
  const cacheKeySource: TSearchQueryState = filtersV2Enabled
    ? watchedQueryStates
    : { q: searchParams.get('q') ?? undefined, filter: searchParams.get('filter') ?? undefined }
  const { currentCacheKey, cacheData: searchPageCache, scrollY } = useSearchCache(cacheKeySource)

  // Back-navigation scroll restoration, DS only (STRFRNT-16138). Search has no
  // `LayoutPaginationWrapper`, so there is nothing to force-load and no return value to consume — the
  // engine re-pins the anchored product card as the grid re-renders from the cache. Legacy keeps the
  // pixel restore inside `useSearchCache`, which stands down under DS. Always called; only `enabled`
  // varies, since the DS decision is per-page and flips between client navigations. `trackState:
  // false` skips the active/targetId useState updates, which this hook doesn't consume.
  useBackNavScrollRestoration({ bands: SEARCH_PRODUCT_CARD_BANDS, enabled: shouldUseDesignSystem(), trackState: false })

  const [productCards, setProductCards] = useState<SearchProductCard[]>(searchPageCache?.products ?? [])
  const [filters, setFilters] = useState<Filter[]>(searchPageCache?.filters ?? [])
  const [isLayoutLoading, setIsLayoutLoading] = useState(searchPageCache ? false : true)
  // Treatment only: a filter refetch in flight. Kept apart from `isLayoutLoading` so the section
  // components dim the grid instead of hiding it, and nothing keyed on the initial load re-runs.
  const [isRefetching, setIsRefetching] = useState(false)
  const [currentPage, setCurrentPage] = useState(searchPageCache?.currentPage ?? 0)
  const [totalPages, setTotalPages] = useState(searchPageCache?.totalPages ?? 0)
  const [totalProducts, setTotalProducts] = useState(searchPageCache?.totalProducts ?? 0)
  const [isError, setIsError] = useState(false)
  const [isMoreProductsLoading, setIsMoreProductsLoading] = useState(false)
  const [hideSearchHeader, setHideSearchHeader] = useState<boolean>(false)

  const widgetInfoRef = useRef(searchPageCache?.widgetInfo ?? {})
  const isInitialCallRef = useRef(true)
  const previousSearchQueryRef = useRef(searchQuery)
  const hasMorePageToLoad = currentPage + 1 <= totalPages

  const shouldTriggerSearchCompleteEvent = useAppSelector<SearchDetails>(
    siteStateManager,
    siteStateManager.getSearchDetails,
  ).shouldTriggerSearchCompleteEvent

  const handleSearchAPIError = useCallback((error: unknown, errorMessage: string) => {
    captureException(error, {
      module: MODULES.SEARCH,
      feature: FEATURES.SEARCH,
      extraInfo: { message: errorMessage },
    })
  }, [])

  useEffect(() => {
    const url = new URL(window.location.href)
    const hideSearchHeaderValue = url.searchParams.get('hide_header') == 'true'
    setHideSearchHeader(hideSearchHeaderValue)

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  const fetchProductsOnFilterUpdate = useCallback(async () => {
    // Cancel any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    abortControllerRef.current = new AbortController()
    const signal = abortControllerRef.current.signal

    // Reset pagination when filters change and fetch filtered products
    setCurrentPage(0)
    if (filtersV2Enabled) {
      setIsRefetching(true)
    } else {
      setIsLayoutLoading(true)
    }
    const apiFilters = getSearchFilters(selectedFilters)
    try {
      const response = await fetchSearchWidgetsData(
        {
          query: searchQuery,
          offset: 0,
          limit: LIMIT,
          lbUserId,
          pageType: PAGE_TYPES.SEARCH,
          widgetInfo: widgetInfoRef.current,
          filter: apiFilters,
        },
        {
          signal,
          headers: searchRequestHeaders,
        },
      )

      // Only update state if the request wasn't aborted
      if (!signal.aborted) {
        const products = response.data.widgets.data?.products ?? []
        const queryId = response.data.widgets.data?.searchEventMetadata?.queryId ?? ''

        const commitResults = () => {
          setCurrentPage(1)
          setTotalPages(response.data.widgets.data?.paginationMetadata?.totalPages ?? 0)
          setTotalProducts(response.data.widgets.data?.paginationMetadata?.totalCount ?? 0)
          setProductCards(transformProductsToProductCards(products, queryId))
          if (filtersV2Enabled) setIsRefetching(false)
        }

        // Treatment: the grid is still on screen, so let the 40-card swap yield to any click that
        // lands mid-render instead of blocking it; un-dim in the same commit as the new cards.
        if (filtersV2Enabled) {
          startTransition(commitResults)
        } else {
          commitResults()
        }
        siteStateManager.setSearchAlgoliaQueryId({
          searchQuery: searchQuery,
          searchAlgoliaQueryId: queryId,
        })
      }
    } catch (e) {
      if (!signal.aborted) {
        setIsError(true)
        setIsRefetching(false)
        handleSearchAPIError(e, FILTER_UPDATE_ERROR_MESSAGE)
      }
    } finally {
      // Only update loading state if this is still the current request
      if (!signal.aborted && !filtersV2Enabled) {
        setIsLayoutLoading(false)
      }
    }
  }, [searchQuery, selectedFilters, searchRequestHeaders, filtersV2Enabled])

  const updateQuery = useCallback(
    ({ filter }: UpdateQueryParams) => {
      const updatedSelectedFilters = getUpdatedSelectedFilters({ filter, selectedFilters })
      const updatedFilterSearchParam = SelectedFilter.getUpdatedSelectedFilterStringForWindowURL(updatedSelectedFilters)

      if (SelectedFilter.checkEquality(updatedSelectedFilters, selectedFilters)) return

      if (filtersV2Enabled) {
        scrollSearchResultsIntoViewIfAbove()
      } else {
        scrollToTop()
      }
      const updatedWatchedQueryState = {
        q: searchQuery,
        filter: updatedFilterSearchParam,
      }
      setWatchedQueryStates(updatedWatchedQueryState, null, false)
      resetSearchCacheOnFilterChange(updatedWatchedQueryState)
    },
    [searchQuery, selectedFilters, filtersV2Enabled],
  )

  useEffect(() => {
    const hasSearchQueryChanged = previousSearchQueryRef.current !== searchQuery
    previousSearchQueryRef.current = searchQuery

    async function fetchInitialResults() {
      try {
        setIsLayoutLoading(true)
        const apiFilters = getSearchFilters(selectedFilters)
        const requestParams = {
          query: searchQuery,
          lbUserId,
          limit: LIMIT,
        }

        let response: AxiosResponse<LayoutResponse>

        if (apiFilters.length > 0) {
          // Make parallel calls when filters are applied. When filters are passed to algolia, it doesnt return complete filter list.
          const [layoutResponseWithoutFilters, layoutResponseWithFilters] = await Promise.all([
            fetchSearchLayout(requestParams, { headers: searchRequestHeaders }),
            fetchSearchLayout(
              {
                ...requestParams,
                filter: apiFilters,
              },
              { headers: searchRequestHeaders },
            ),
          ])
          const filtersData = convertFacetsToInitialFilters(
            layoutResponseWithoutFilters.data.widgets[0]?.data?.facetsList,
          )
          setFilters(filtersData)
          response = layoutResponseWithFilters
        } else {
          // Single call when no filters are applied
          response = await fetchSearchLayout(requestParams, { headers: searchRequestHeaders })
          const filtersData = convertFacetsToInitialFilters(response.data.widgets[0]?.data?.facetsList)
          setFilters(filtersData)
        }

        const initialQueryId = response.data.widgets[0]?.data?.searchEventMetadata?.queryId ?? ''

        setCurrentPage(1)
        widgetInfoRef.current = response.data.widgets[0]?.widgetInfo ?? {}
        setTotalPages(response.data.widgets[0]?.data?.paginationMetadata?.totalPages ?? 0)
        setTotalProducts(response.data.widgets[0]?.data?.paginationMetadata?.totalCount ?? 0)
        setProductCards(transformProductsToProductCards(response.data.widgets[0]?.data?.products ?? [], initialQueryId))

        // Note: Event is trigerred only when user updates the search query from searchBox.
        if (shouldTriggerSearchCompleteEvent) {
          const eventData = getSearchCompletedEventData({
            data: response.data.widgets[0]?.data?.products ?? [],
            searchQuery: searchQuery.trim(),
            numberOfSearchResults: response.data.widgets[0]?.data?.paginationMetadata?.totalCount ?? 0,
          })
          trackEvent(TELEMETRY_EVENT_NAMES.SEARCH_COMPLETED, eventData)
          siteStateManager.setShouldTriggerSearchCompleteEvent(false)
        }
        siteStateManager.setSearchAlgoliaQueryId({
          searchQuery: searchQuery,
          searchAlgoliaQueryId: initialQueryId,
        })
      } catch (e) {
        setIsError(true)
        handleSearchAPIError(e, INITIAL_SEARCH_ERROR_MESSAGE)
      }
      setIsLayoutLoading(false)
    }

    if (!searchPageCache) {
      if (isInitialCallRef.current || hasSearchQueryChanged) {
        // For initial call
        scrollToTop()
        void fetchInitialResults()
        isInitialCallRef.current = false
      } else {
        // For filter update. Treatment already placed the viewport in `updateQuery`; a smooth scroll
        // here would animate over the frames the dimmed grid needs to swap in the new results.
        if (!filtersV2Enabled) scrollToTop()
        void fetchProductsOnFilterUpdate()
      }
    } else {
      setCurrentPage(searchPageCache.currentPage)
      setTotalPages(searchPageCache.totalPages)
      setTotalProducts(searchPageCache.totalProducts)
      setProductCards(searchPageCache.products)
      setFilters(searchPageCache.filters)
      widgetInfoRef.current = searchPageCache.widgetInfo
    }

    if (searchQuery) {
      trackEvent(TELEMETRY_EVENT_NAMES.SEARCH, {
        searchQuery: searchQuery.trim(),
      })
    }
  }, [watchedQueryStates])

  const intersectionOffset = isBrowser() ? window.innerHeight / 4 : 200
  const [intersectRef, isIntersecting] = useIntersect(null, `${intersectionOffset}px`, 0, false, !hasMorePageToLoad)

  const onLoadMore = useCallback(
    async ({ appliedFilters = selectedFilters }: { appliedFilters?: SelectedFilter }) => {
      if (isMoreProductsLoading || isLayoutLoading || isRefetching) return

      setIsMoreProductsLoading(true)
      let response: AxiosResponse<WidgetFetchDataResponse> | undefined
      try {
        const apiFilters = getSearchFilters(appliedFilters)

        response = await fetchSearchWidgetsData(
          {
            query: searchQuery,
            offset: currentPage * LIMIT,
            limit: LIMIT,
            lbUserId,
            pageType: PAGE_TYPES.SEARCH,
            widgetInfo: widgetInfoRef.current,
            filter: apiFilters,
            environment: getContentProductsEnv(),
          },
          { headers: searchRequestHeaders },
        )

        setCurrentPage(prev => prev + 1)
        const moreProducts = response.data.widgets.data?.products ?? []
        const loadMoreQueryId = response.data.widgets.data?.searchEventMetadata?.queryId ?? ''
        setProductCards(prev => {
          const nextCards = transformProductsToProductCards(moreProducts, loadMoreQueryId)
          if (!filtersV2Enabled) return [...prev, ...nextCards]
          // Treatment renders cards keyed by `getSearchCardKey`, so a page overlapping the previous one
          // (ranking drift between requests) must not append a duplicate key.
          const seen = new Set(prev.map(getSearchCardKey))
          return [...prev, ...nextCards.filter(card => !seen.has(getSearchCardKey(card)))]
        })
        siteStateManager.setSearchAlgoliaQueryId({
          searchQuery: searchQuery,
          searchAlgoliaQueryId: loadMoreQueryId,
        })
      } catch (e) {
        handleSearchAPIError(e, LOAD_MORE_ERROR_MESSAGE)
      }
      setIsMoreProductsLoading(false)
    },
    [
      selectedFilters,
      isMoreProductsLoading,
      isLayoutLoading,
      isRefetching,
      searchQuery,
      currentPage,
      searchRequestHeaders,
      filtersV2Enabled,
    ],
  )

  // `isRefetching` is a dep so a treatment refetch that lands while the sentinel is already on screen
  // (grid stayed mounted, so the observer never re-fires) still auto-loads the next page. Control's
  // hide/show of the section re-fires the observer on its own; `isRefetching` never changes there.
  useEffect(() => {
    if (isIntersecting && !isMoreProductsLoading && !isLayoutLoading && !isRefetching && hasMorePageToLoad) {
      void onLoadMore({ appliedFilters: selectedFilters })
    }
  }, [isIntersecting, isRefetching])

  // Treatment: no snapshot while a fetch is in flight or after one failed. `setCurrentPage(0)` re-runs
  // this effect on the click itself, and a rejected fetch re-runs it from `finally`, both while the key
  // already points at the new filter state but `productCards` still holds the previous results — the
  // write would cache them under the wrong key, and a later Back would restore them silently. Control
  // writes unconditionally as before; its lagging URL key sends that write to the old entry.
  useEffect(() => {
    if (filtersV2Enabled && (isLayoutLoading || isRefetching || isError)) return

    queueMicrotask(() => {
      searchCacheStateManager.setSearchCache(
        {
          currentPage: currentPage,
          totalPages: totalPages,
          totalProducts: totalProducts,
          products: productCards,
          filters: filters,
          widgetInfo: widgetInfoRef.current,
          scrollY: scrollY.current,
        },
        currentCacheKey.current,
      )
    })
  }, [
    currentCacheKey,
    productCards,
    filters,
    scrollY,
    currentPage,
    totalPages,
    totalProducts,
    isLayoutLoading,
    isRefetching,
    isError,
    filtersV2Enabled,
  ])

  return {
    productCards,
    isLayoutLoading,
    isRefetching,
    filtersV2Enabled,
    isError,
    searchQuery,
    intersectRef,
    isMoreProductsLoading,
    totalProducts: totalProducts,
    filters,
    selectedFilters,
    updateQuery,
    hideSearchHeader,
  }
}
